import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { SpeakerSelect } from "@/components/quotes/SpeakerSelect";
import { QuoteLinksFields } from "@/components/quotes/QuoteLinksFields";

const toLine = (ln) => ({ speaker: ln.entity_id ? `link:${ln.entity_id}` : "none", name: ln.entity_id ? "" : ln.speaker_name || "", text: ln.text });

export const QuoteEditor = ({ uid, quote, entities, defaultSpeaker, onClose, onSaved }) => {
  const [lines, setLines] = useState(quote ? quote.lines.map(toLine) : [{ speaker: defaultSpeaker ? `link:${defaultSpeaker}` : "none", name: "", text: "" }]);
  const [context, setContext] = useState(quote?.context || "");
  const [tags, setTags] = useState(quote?.tags || []);
  const [eventIds, setEventIds] = useState(quote?.event_ids || []);
  const [busy, setBusy] = useState(false);
  const upd = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    setBusy(true);
    const body = {
      context, tags, event_ids: eventIds,
      lines: lines.filter((l) => l.text.trim()).map((l) => (l.speaker.startsWith("link:") ? { entity_id: l.speaker.slice(5), text: l.text } : { speaker_name: l.name, text: l.text })),
    };
    try {
      if (quote) await api.patch(`/quotes/${quote.id}`, body);
      else await api.post(`/universes/${uid}/quotes`, body);
      toast.success(quote ? "Quote updated" : "Quote added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto lf-scroll" data-testid="quote-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{quote ? "Edit quote" : "Add a quote"}</DialogTitle>
          <DialogDescription>Add more lines to make it a conversation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {lines.map((l, i) => (
            <div key={i} className="space-y-2 rounded-md border border-[#2D3442] p-3" data-testid={`quote-editor-line-${i}`}>
              <div className="flex gap-2">
                <SpeakerSelect entities={entities} value={l.speaker} onChange={(v) => upd(i, { speaker: v })} extra={[{ value: "none", label: "Unattributed" }]} testid={`quote-editor-speaker-${i}`} className="h-9 flex-1" />
                {lines.length > 1 && <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`quote-editor-remove-${i}`}><X className="h-4 w-4" /></Button>}
              </div>
              {l.speaker === "none" && <Input value={l.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Speaker name if known (optional)" className="h-9" data-testid={`quote-editor-name-${i}`} />}
              <Textarea rows={2} value={l.text} onChange={(e) => upd(i, { text: e.target.value })} placeholder="What was said" className="font-display text-lg" data-testid={`quote-editor-text-${i}`} />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLines([...lines, { speaker: "none", name: "", text: "" }])} data-testid="quote-editor-add-line"><Plus className="mr-1 h-3.5 w-3.5" />Add line</Button>
          <Input value={context} onChange={(e) => setContext(e.target.value)} placeholder="Context: to whom, when, where (optional)" data-testid="quote-editor-context" />
          <QuoteLinksFields uid={uid} tags={tags} eventIds={eventIds} onTags={setTags} onEvents={setEventIds} testid="quote-editor-links" />
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !lines.some((l) => l.text.trim())} data-testid="quote-editor-save">{busy ? "Saving…" : "Save quote"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
